import React, { useEffect, useState } from 'react';
import { subDays, startOfDay, endOfDay } from 'date-fns';
import {
  FormControl,
  TextField,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DynamicTable } from 'components';
import { axiosInstance } from 'utils';
import { DateUtil } from 'doctivity-shared/utils';
import { UsageOverTimeChart } from 'components/usage/UsageOverTimeChart';
import { UsageBubble } from 'components/usage/UsageBubble';
import { UsageHeatmap } from 'components/usage/UsageHeatmap';

const HEATMAP_METRICS = [
  { key: 'percent_active', label: '% Active' },
  { key: 'logins', label: 'Logins' },
  { key: 'notes', label: 'Notes' },
  { key: 'page_views', label: 'Pages' },
  { key: 'ai', label: 'AI' },
  { key: 'data_edits', label: 'Edits' },
  { key: 'views', label: 'Views' },
];
const fmtDuration = (sec) => {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};
const metricVal = (r, key) => (key === 'ai'
  ? (Number(r.ai_text) || 0) + (Number(r.ai_voice) || 0)
  : Number(r[key]) || 0);

const COMPARE_METRICS = [
  { value: 'active_users', label: 'Active Users' },
  { value: 'percent_active', label: '% Active' },
  { value: 'logins', label: 'Logins' },
  { value: 'notes', label: 'Activity Notes' },
  { value: 'data_edits', label: 'Data Edits' },
  { value: 'views', label: 'Record Views' },
  { value: 'page_views', label: 'Page Views' },
  { value: 'median_session_seconds', label: 'Median Time on Site' },
  { value: 'ai_text', label: 'AI Text' },
  { value: 'ai_voice', label: 'AI Voice' },
];
const minimumStartDate = new Date(2018, 0, 1);

// Client-vs-client tab with its own filtering: a metric selector (which
// column ranks the bar chart) plus an independent From/To date range.
export function UsageCompareTab() {
  const [startDate, setStartDate] = useState(startOfDay(subDays(new Date(), 30)));
  const [endDate, setEndDate] = useState(endOfDay(new Date()));
  const [metric, setMetric] = useState('active_users');
  const [summary, setSummary] = useState(null);
  const [query, setQuery] = useState({ limit: 100, offset: 0 });

  const startISO = startDate.toISOString();
  const endISO = endDate.toISOString();

  useEffect(() => {
    let alive = true;
    axiosInstance
      .post('/UsageAnalytics', { type: 'USAGE_CLIENT_SUMMARY', start: startISO, end: endISO })
      .then((r) => { if (alive) setSummary(r.data); })
      .catch(() => { if (alive) setSummary({ rows: [] }); });
    return () => { alive = false; };
  }, [startISO, endISO]);

  const rows = (summary && summary.rows) || [];
  const metricDef = COMPARE_METRICS.find((m) => m.value === metric) || COMPARE_METRICS[0];
  const sorted = rows.slice().sort((a, b) => (Number(b[metric]) || 0) - (Number(a[metric]) || 0));
  const top = sorted.slice(0, 15);

  const series = [{ name: metricDef.label, data: top.map((r) => Number(r[metric]) || 0) }];
  const categories = top.map((r) => r.client_name);

  // Value-axis formatting depends on the chosen metric: percent gets a '%',
  // time-on-site gets a duration, everything else is a whole-number count.
  const isPercent = metric === 'percent_active';
  const isDuration = metric === 'median_session_seconds';
  let metricFormatter;
  if (isPercent) metricFormatter = (v) => `${Math.round(v)}%`;
  else if (isDuration) metricFormatter = (v) => fmtDuration(v);
  const metricIsInteger = !isPercent && !isDuration;

  // Health quadrant: x = total users, y = % active, z = overall activity.
  const bubblePoints = rows
    .filter((r) => Number(r.total_users) > 0)
    .map((r) => ({
      x: Number(r.total_users) || 0,
      y: Number(r.percent_active) || 0,
      z: ['logins', 'notes', 'data_edits', 'views', 'ai_text', 'ai_voice']
        .reduce((s, k) => s + (Number(r[k]) || 0), 0),
      name: r.client_name,
    }));

  // Heatmap: top clients (by active users) x metrics, colour = per-column
  // normalized 0-100, raw value in the tooltip.
  const heatClients = rows.slice().sort((a, b) => (b.active_users || 0) - (a.active_users || 0)).slice(0, 12);
  const colMax = {};
  HEATMAP_METRICS.forEach((m) => {
    colMax[m.key] = Math.max(1, ...heatClients.map((r) => metricVal(r, m.key)));
  });
  const heatSeries = heatClients
    .map((r) => ({
      name: r.client_name,
      data: HEATMAP_METRICS.map((m) => {
        const raw = metricVal(r, m.key);
        return { x: m.label, y: Math.round((raw / colMax[m.key]) * 100), raw };
      }),
    }))
    .reverse();

  const columns = [
    { label: 'Client', key: 'client_name' },
    { label: 'Total Users', key: 'total_users' },
    { label: 'Active Users', key: 'active_users' },
    { label: '% Active', key: 'percent_active', format: (v) => `${v}%` },
    { label: 'Logins', key: 'logins' },
    { label: 'Notes', key: 'notes' },
    { label: 'Page Views', key: 'page_views' },
    { label: 'Median Time', key: 'median_session_seconds', format: (v) => fmtDuration(v) },
    { label: 'Data Edits', key: 'data_edits' },
    { label: 'Views', key: 'views' },
    { label: 'AI Text', key: 'ai_text' },
    { label: 'AI Voice', key: 'ai_voice' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <FormControl style={{ minWidth: 200 }}>
          <InputLabel>Metric</InputLabel>
          <Select value={metric} label='Metric' onChange={(e) => setMetric(e.target.value)}>
            {COMPARE_METRICS.map((m) => (
              <MenuItem value={m.value} key={m.value}>{m.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 140 }}>
          <DatePicker
            label='From'
            value={startDate}
            minDate={minimumStartDate}
            maxDate={endDate}
            onChange={(d) => d && setStartDate(startOfDay(d))}
            format={DateUtil.LDML_DISPLAY_FORMAT}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
        <FormControl style={{ minWidth: 140 }}>
          <DatePicker
            label='To'
            value={endDate}
            minDate={startDate}
            maxDate={new Date()}
            onChange={(d) => d && setEndDate(endOfDay(d))}
            format={DateUtil.LDML_DISPLAY_FORMAT}
            renderInput={(params) => <TextField {...params} />}
          />
        </FormControl>
      </div>

      <UsageOverTimeChart
        type='bar'
        horizontal
        title={`${metricDef.label} by Client (top 15)`}
        categories={categories}
        series={series}
        valueFormatter={metricFormatter}
        integer={metricIsInteger}
        height={Math.max(top.length * 26 + 120, 300)}
      />

      <UsageBubble
        title='Client Health Quadrant'
        subtitle='x = total users · y = % active · bubble size = overall activity'
        points={bubblePoints}
        xTitle='Total users'
        yTitle='% active'
      />

      <UsageHeatmap
        title='Customer Health Heatmap'
        subtitle='Colour = relative intensity within each metric column; hover for raw values'
        series={heatSeries}
      />

      <div style={{ marginTop: 16 }}>
        <DynamicTable
          header='All Clients'
          columns={columns}
          data={sorted}
          query={query}
          onQueryChange={setQuery}
          idKey='client_id'
          noDataMessage='No client usage in this range'
        />
      </div>
    </div>
  );
}
